import { useLogger } from '~/composables/useLogger'
import { isHydratingServerRender } from '~/utils/isHydratingServerRender'
import { identityStartupInitialization } from '~/utils/identityStartupInitialization'
import { refreshCurrentIdentity } from '~/utils/refreshCurrentIdentity'
import { refreshStoredIdentity } from '~/utils/refreshStoredIdentity'
import { sanctumTokenStorage } from '~/utils/sanctumTokenStorage'

export default defineNuxtPlugin((nuxtApp) => {
  const logger = useLogger('IdentityInitialLoad')
  const user = useSanctumUser()
  const { redirect } = useSanctumConfig()
  const route = useRoute()

  const requestInitialization = (): void => {
    if (user.value !== null || sanctumTokenStorage.read() === undefined) return
    identityStartupInitialization.request(nuxtApp)
  }
  const completeInitialization = (): void => {
    identityStartupInitialization.consume(nuxtApp)
  }
  nuxtApp.hook('sanctum:init', completeInitialization)
  nuxtApp.hook('sanctum:login', completeInitialization)
  if (isHydratingServerRender(nuxtApp)) {
    try {
      requestInitialization()
    } catch (error: unknown) {
      logger.warn('storedCredentialUnverified', { error })
    }
  }

  const loadStoredIdentity = async (): Promise<void> => {
    try {
      requestInitialization()
      await refreshStoredIdentity(nuxtApp, async () => {
        if (user.value !== null) return
        await refreshCurrentIdentity(nuxtApp, logger, { initialize: true })
      })
    } catch (error: unknown) {
      logger.warn('storedCredentialUnverified', { error })
    }
  }

  const leaveGuestOnlyRoute = async (): Promise<void> => {
    if (user.value === null || sanctumTokenStorage.read() === undefined) return
    const target = redirect.onGuestOnly
    const { guestOnly, excluded } = route.meta.sanctum ?? {}
    if (excluded === true || typeof target !== 'string') return
    const path = route.path.length > 1 ? route.path.replace(/\/$/, '') : route.path
    if (guestOnly !== true && path !== redirect.onAuthOnly) return
    await nuxtApp.runWithContext(() => navigateTo(target, { replace: true }))
  }

  let canRedirectAfterHydration = true
  let hasCompletedInitialNavigation = false
  nuxtApp.hook('app:created', () => {
    hasCompletedInitialNavigation = true
  })
  nuxtApp.hook('page:loading:start', async () => {
    if (hasCompletedInitialNavigation) canRedirectAfterHydration = false
    if (isHydratingServerRender(nuxtApp)) return
    canRedirectAfterHydration = false
    await loadStoredIdentity()
  })

  // Server-rendered markup keeps its guest identity until async layouts finish hydrating.
  if (isHydratingServerRender(nuxtApp)) {
    nuxtApp.hook('app:suspense:resolve', async () => {
      const token = sanctumTokenStorage.read()
      await loadStoredIdentity()
      if (!canRedirectAfterHydration || token === undefined || sanctumTokenStorage.read() !== token)
        return
      await leaveGuestOnlyRoute()
    })
  }
})
